import { useMemo, type MouseEventHandler, type ReactNode } from 'react'
import { TDrawerBottomActions, TListItem, TSection } from 'app/components/terros'
import { PermissionTooltip, ViewPermissionButton } from 'app/components/permissions'
import { PrefetchedUserInfo } from 'app/components/entityInfo'
import { ConditionTooltip } from 'app/components/ConditionTooltip'
import { ButtonDelete, TButton } from 'app/components/buttons'
import { TAuditInfoSection } from 'app/components/auditInfo'
import {
  compareRoles,
  getName,
  isManagerRole,
  isNotEmpty,
  roleCanBeInTeamMember,
  unique,
  type RoleId,
  type TeamGetSuccess,
  type TeamMemberData,
} from '@terros/common'
import { Link } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { Group, Loader, Stack, Switch } from '@mantine/core'
import { useToastError } from '@hooks'
import {
  translateLabels,
  usePermissionCheck,
  usePreference,
  useRemoveTeamMember,
  useRoles,
  useTranslation,
  useUpdateTeamMember,
} from '@front/model'
import { ManagerInfo } from './ManagerInfo'

export const UserTeamMembership = ({
  teamData,
  userMembership,
}: {
  teamData: TeamGetSuccess
  userMembership: TeamMemberData
}): ReactNode => {
  const { t } = useTranslation()
  const { data: allRoles, isLoading } = useRoles({ companyId: userMembership.user.companyId })
  const { update, isUpdating, error: updateError } = useUpdateTeamMember()
  useToastError(updateError)
  const { remove, isRemoving, error: removeError } = useRemoveTeamMember()
  useToastError(removeError)
  const { data: managerBasedTeams = false } = usePreference('managerBasedTeams', teamData.team.teamId)

  const membershipRoles = useMemo(() => allRoles?.filter(roleCanBeInTeamMember).sort(compareRoles), [allRoles])

  const { team } = teamData

  const { user, roleIds, locked } = userMembership

  const onRemove: MouseEventHandler<HTMLButtonElement> = async (e): Promise<void> => {
    e.preventDefault()
    if (team.isDeleted) return
    await remove({ teamId: team.teamId, userId: user.userId })
  }

  const toggleMembershipRole = async (roleId: RoleId, value: boolean): Promise<void> => {
    if (team.isDeleted) return
    const newRoles = value ? unique([...(roleIds ?? []), roleId]) : (roleIds ?? []).filter((r) => r !== roleId)

    await update({
      userId: user.userId,
      teamId: teamData.team.teamId,
      roleIds: newRoles,
      locked,
    })
  }

  const { data: hasUpdatePermission } = usePermissionCheck({
    permissionType: 'team:update',
    targetId: teamData.team.teamId,
  })

  const canUpdateUserMembership = !team.isDeleted && hasUpdatePermission

  const toggleMembershipLock = async (value: boolean): Promise<void> => {
    if (team.isDeleted) return
    await update({
      userId: user.userId,
      teamId: teamData.team.teamId,
      roleIds,
      locked: value,
    })
    const message = value ? 'Membership locked' : 'Membership unlocked'
    notifications.show({
      message,
    })
  }

  return (
    <>
      <Stack>
        <Group justify='space-between'>
          <PrefetchedUserInfo linked={false} user={user} />
          <TButton component={Link} icon='open_in_new' iconPosition='right' to={`/user/${user.userId}`} variant='ghost'>
            {t('View User')}
          </TButton>
        </Group>
        {isLoading && <Loader />}
        {managerBasedTeams && <ManagerInfo user={user} team={teamData.team} />}
        {isNotEmpty(membershipRoles) && (
          <TSection heading={{ title: t('Roles') }}>
            {membershipRoles.map((role) => {
              return (
                <ConditionTooltip
                  refProp='rootRef'
                  key={role.roleId}
                  conditions={translateLabels(
                    [
                      {
                        active: team.isDeleted === true,
                        label: 'Restore this team from Trash before making changes.',
                      },
                      {
                        active: isUpdating,
                        label: 'Loading...',
                      },
                      {
                        active: !canUpdateUserMembership,
                        label: 'You do not have permission to update this team.',
                      },
                      {
                        active: role.canAssign === false,
                        label: 'You do not have permission to assign this role.',
                      },
                      {
                        active: managerBasedTeams && isManagerRole(role),
                        label: 'Manager roles are managed automatically and cannot be changed manually.',
                      },
                    ],
                    t
                  )}
                >
                  <Switch
                    checked={roleIds?.includes(role.roleId)}
                    description={role.description}
                    disabled={isUpdating || !canUpdateUserMembership}
                    key={role.roleId}
                    label={role.name}
                    onChange={(event) => {
                      toggleMembershipRole(role.roleId, event.target.checked)
                    }}
                  />
                </ConditionTooltip>
              )
            })}
          </TSection>
        )}
        <TSection heading={{ title: t('Advanced') }}>
          <Switch
            checked={locked}
            description={t(
              'Locked membership will prevent the user from being removed from the team by other workflow actions.'
            )}
            disabled={isUpdating || !canUpdateUserMembership}
            label={t('Lock Membership')}
            onChange={(event) => {
              toggleMembershipLock(event.target.checked)
            }}
          />
          <ViewPermissionButton
            drawerTitle={t(`Permissions for {{name}}`, { name: getName(user) })}
            entityId={user.userId}
            permissionType='user:manage'
          />
        </TSection>
        <TAuditInfoSection {...userMembership}>
          <TListItem label='User ID' details={user.userId} canCopy />
          <TListItem label='Team ID' details={team.teamId} canCopy />
        </TAuditInfoSection>
      </Stack>
      {!team.isDeleted && !managerBasedTeams && (
        <TDrawerBottomActions>
          <PermissionTooltip permissionType='team:update' targetId={team.teamId}>
            <ButtonDelete loading={isRemoving} onClick={onRemove}>
              {t('Remove from team')}
            </ButtonDelete>
          </PermissionTooltip>
        </TDrawerBottomActions>
      )}
    </>
  )
}
